import { useState, useCallback } from 'react';
import { routeService, RoutesListParams } from '../services/routeService';
import { SingleRoute } from '../../../Resources/Data/Routes';

export interface UseRoutesState {
  routes: SingleRoute[];
  totalRoutes: number;
  currentPage: number;
  totalPages: number;
  loading: boolean;
  error: string | null;
}

export interface UseRoutesActions {
  fetchRoutes: (params?: RoutesListParams) => Promise<void>;
  activateRoute: (id: string | number) => Promise<boolean>;
  deactivateRoute: (id: string | number, rejectionReason?: string) => Promise<boolean>;
  setCurrentPage: (page: number) => void;
  refreshRoutes: () => Promise<void>;
}

export const useRoutes = (initialParams: RoutesListParams = {}) => {
  const [state, setState] = useState<UseRoutesState>({
    routes: [],
    totalRoutes: 0,
    currentPage: initialParams.page || 1,
    totalPages: 0,
    loading: false,
    error: null
  });

  const [filters, setFilters] = useState<RoutesListParams>(initialParams);

  const fetchRoutes = useCallback(async (params?: RoutesListParams) => {
    const queryParams = params || filters;

    setState(prev => ({ ...prev, loading: true, error: null }));

    try {
      const response = await routeService.getRoutes({
        ...queryParams,
        page: queryParams.page || state.currentPage
      });

      const { routes, count } = response.data;
      const pageSize = queryParams.pageSize || 12;
      const totalPages = Math.ceil(count / pageSize);

      setState(prev => ({
        ...prev,
        routes,
        totalRoutes: count,
        totalPages,
        loading: false
      }));

      if (params) {
        setFilters(params);
      }
    } catch (error) {
      console.error('Error fetching routes:', error);
      setState(prev => ({
        ...prev,
        loading: false,
        error: 'Failed to fetch routes. Please try again.'
      }));
    }
  }, []);

  const activateRoute = async (id: string | number): Promise<boolean> => {
    try {
      const response = await routeService.activateRoute(id);

      if (response.status === 200) {
        setState(prev => ({
          ...prev,
          routes: prev.routes.map(route =>
            route.id === Number(id)
              ? { ...route, status: 'active' as any }
              : route
          )
        }));
        return true;
      }

      return false;
    } catch (error) {
      console.error('Failed to activate route:', error);
      setState(prev => ({
        ...prev,
        error: 'Failed to activate route. Please try again.'
      }));
      return false;
    }
  };

  const deactivateRoute = async (id: string | number, rejectionReason?: string): Promise<boolean> => {
    try {
      const response = await routeService.deactivateRoute(id, rejectionReason);

      if (response.status === 200) {
        setState(prev => ({
          ...prev,
          routes: prev.routes.map(route =>
            route.id === Number(id)
              ? { ...route, status: 'inactive' as any }
              : route
          )
        }));
        return true;
      }

      return false;
    } catch (error) {
      console.error('Failed to deactivate route:', error);
      setState(prev => ({
        ...prev,
        error: 'Failed to deactivate route. Please try again.'
      }));
      return false;
    }
  };

  const setCurrentPage = (page: number) => {
    setState(prev => ({ ...prev, currentPage: page }));
  };

  const refreshRoutes = async () => {
    await fetchRoutes(filters);
  };

  const actions: UseRoutesActions = {
    fetchRoutes,
    activateRoute,
    deactivateRoute,
    setCurrentPage,
    refreshRoutes
  };

  return {
    ...state,
    ...actions,
    filters
  };
};
